import * as React from 'react';
import { StyleSheet, css, CSSProperties } from 'aphrodite/no-important';
import { useTheme } from '../../../../theming/useTheme';
import { DecorationLine } from '../../DecorationLine';
import { VrLineHeight } from '../../../../theming/verticalRhythm/verticalRhythm';
import { CardComponentDataProps } from '../../cards/dividedCard/DividedCard';
import { ListItem } from '../listItem/ListItem';
import { LIST_STYLE, LIST_WRAPPER_STYLE } from '../lists.const';
import { MEDIA_BREAKPOINTS } from '../../../../mediaQueries.const';

const componentConfigurationKey = 'listWithHeading';
const createStyles = (
  titleColor: string,
  backgroundColor: string,
  vrLineHeight: VrLineHeight,
) => StyleSheet.create({
  listWrapper: {
    ...LIST_WRAPPER_STYLE,
    backgroundColor,
  },
  list: {
    ...LIST_STYLE,
    [MEDIA_BREAKPOINTS.SMALL]: {
      position: 'relative',
    },
  },
  title: {
    color: titleColor,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`,
    [MEDIA_BREAKPOINTS.LARGE]: {
      padding: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 2}px`,
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      padding: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`,
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      padding: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 2}px`,
    },
  },
});

interface ListSectionComponentDataProps {
  listProps: CardComponentDataProps[]
  displayName: string
  itemNumLines?: number
}

interface ListSectionComponentThemeProps {
  vrLineHeight: VrLineHeight
  titleColor: string
  backgroundColor: string
  surfaceStyle: CSSProperties
}

type ListSectionComponentProps = ListSectionComponentDataProps & ListSectionComponentThemeProps;

export const ListSectionComponent: React.FunctionComponent<ListSectionComponentProps> = (
  {
    listProps,
    displayName,
    titleColor,
    backgroundColor,
    vrLineHeight,
    surfaceStyle,
    itemNumLines,
  },
) => {
  const styles = createStyles(
    titleColor,
    backgroundColor,
    vrLineHeight,
  );

  const createHeading = () => (
    <h1 className={css(styles.title)}>
      {displayName}
    </h1>
  );

  return (
    <div className={css(
      StyleSheet.create({ surfaceStyle }).surfaceStyle,
      styles.listWrapper,
    )}
    >
      <DecorationLine />
      <div className={css(styles.list)}>
        {createHeading()}
        {listProps.map((listItemProps, index) => (<ListItem {...listItemProps} numberOfLines={itemNumLines} key={index} />))}
      </div>
    </div>
  );
};

ListSectionComponent.defaultProps = {
  listProps: [],
  displayName: '',
  itemNumLines: 1,
};

const mapThemeToProps = (theme: ListSectionComponentThemeProps) => ({
  titleColor: theme.titleColor,
  backgroundColor: theme.backgroundColor,
  vrLineHeight: theme.vrLineHeight,
  surfaceStyle: theme.surfaceStyle,
});

export const ListWithHeading = (props: ListSectionComponentDataProps) => {
  const themeContext = useTheme(componentConfigurationKey, mapThemeToProps);
  return <ListSectionComponent {...themeContext} {...props} />;
};
